import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, LineChart, Line } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#818cf8', '#4f46e5', '#3730a3'];
const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const formatINR = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val?.toLocaleString('en-IN') || '0';
};

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mo)]} ${y?.slice(2)}`;
};

export function CRMPipelineChart({ data }) {
  if (!data?.length) return null;
  return (
    <Card data-testid="crm-pipeline-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">CRM Pipeline</CardTitle>
        <CardDescription>Leads by status with deal values</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={formatINR} />
            <Tooltip formatter={(val, name) => name === 'Deal Value' ? `Rs. ${formatINR(val)}` : val} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="count" name="Leads" fill="#6366f1" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="deal_value" name="Deal Value" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function TimesheetTrendChart({ data }) {
  if (!data?.length) return null;
  return (
    <Card data-testid="timesheet-trend-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Timesheet Hours</CardTitle>
        <CardDescription>Monthly billable vs non-billable hours</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={monthLabel} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="billable" name="Billable" stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} stackId="1" />
            <Area type="monotone" dataKey="non_billable" name="Non-billable" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} stackId="1" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function ExpenseBreakdownChart({ data }) {
  if (!data?.length) return null;
  const total = data.reduce((s, d) => s + d.total, 0);
  return (
    <Card data-testid="expense-breakdown-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Expense Breakdown</CardTitle>
        <CardDescription>By category (Total: Rs. {formatINR(total)})</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="total" nameKey="label" label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => `Rs. ${formatINR(v)}`} />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function DepartmentChart({ data }) {
  if (!data?.length) return null;
  return (
    <Card data-testid="department-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Team Distribution</CardTitle>
        <CardDescription>Employees by department</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 10, bottom: 5, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={80} />
            <Tooltip />
            <Bar dataKey="count" name="Employees" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function HeadcountChart({ data }) {
  if (!data?.length) return null;
  return (
    <Card data-testid="headcount-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Headcount Growth</CardTitle>
        <CardDescription>Employee growth over time</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={monthLabel} />
            <Line type="monotone" dataKey="headcount" name="Headcount" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
